if (!customElements.get('info-drawer')) {
  class InfoDrawer extends HTMLElement {
    connectedCallback() {
      this.lastTrigger = null;
      this._handleKeydown = this._handleKeydown.bind(this);

      // Backdrop click closes (only when clicking the drawer itself, not the panel)
      this.addEventListener('click', (e) => {
        if (e.target === this) this.close();
      });

      // Any descendant with [data-drawer-close] closes
      this.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('[data-drawer-close]');
        if (closeBtn && this.contains(closeBtn)) this.close();
      });

      this.panel = this.querySelector('.info-drawer__panel');

      // A drawer can re-connect (theme-editor section reload, other-features
      // hoisting dialogs to <body>). Without this guard each re-connect would
      // stack another ResizeObserver.
      if (this._observersBound) return;
      this._observersBound = true;

      this._initHeightAnimation();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._handleKeydown);
    }

    /* Drawers with data-fixed-panel-height take their height from CSS at EVERY
     * breakpoint — no JS pinning, no content tween. On mobile because pinning
     * breaks the iOS keyboard and the full bottom-sheet layout (PR #229); on
     * desktop because the search drawer's height is fixed by design (60vh) and
     * animating it on every query change read as restless. AAS-5265 briefly
     * narrowed this to mobile-only to win back a desktop tween — that tween is
     * what AAS-5373 removes, so the opt-out is global again. */
    _hasFixedPanelHeight() {
      return this.hasAttribute('data-fixed-panel-height');
    }

    // Smoothly tween the panel height when its CONTENT changes size (e.g.
    // predictive search results loading) instead of snapping abruptly. While
    // open the panel is pinned to an explicit pixel height, so new content does
    // not instantly resize it — the body scrolls/clips until the content
    // settles, then we animate to the new height and reveal it.
    _initHeightAnimation() {
      // CSS owns the height on these — skip the observer entirely rather than
      // let it fire and bail on every content change. AAS-5373
      if (this._hasFixedPanelHeight()) return;
      if (!this.panel || typeof ResizeObserver === 'undefined') return;

      this._lastHeight = null;
      this._settleTimer = 0;
      // How long the content must stop changing size before we resize, so the
      // clear → spinner → results burst collapses into a single tween.
      this._settleDelay = 100;

      this._resizeObserver = new ResizeObserver(() => {
        if (this._hasFixedPanelHeight()) return;
        if (this.getAttribute('aria-hidden') !== 'false') return;
        clearTimeout(this._settleTimer);
        this._settleTimer = setTimeout(() => {
          requestAnimationFrame(() => this._tweenHeight());
        }, this._settleDelay);
      });

      this._heightTargets().forEach((el) => this._resizeObserver.observe(el));
    }

    // Elements whose intrinsic size reflects content. For a scrollable flex
    // child (overflow auto/scroll) we watch its children instead, because the
    // child's own box resizes with the panel and would loop the observer.
    _heightTargets() {
      const targets = [];
      Array.from(this.panel.children).forEach((child) => {
        const overflowY = getComputedStyle(child).overflowY;
        const scrolls = overflowY === 'auto' || overflowY === 'scroll';
        const kids = scrolls ? Array.from(child.children) : [];
        if (kids.length) {
          kids.forEach((kid) => targets.push(kid));
        } else {
          targets.push(child);
        }
      });
      return targets;
    }

    // Pin the panel to a fixed pixel height — its natural content height
    // (capped by the CSS max-height) unless an explicit value is given.
    // Measured with the inline height CLEARED, not forced to auto: a panel
    // whose stylesheet fixes its height (e.g. the wishlist's mobile
    // `--tall` 57rem sheet) must pin to THAT, not collapse to content.
    _pinHeight(value) {
      const panel = this.panel;
      let target = value;
      if (target == null) {
        const prev = panel.style.height;
        panel.style.height = '';
        target = panel.getBoundingClientRect().height;
        panel.style.height = prev;
      }
      panel.style.height = `${target}px`;
      this._lastHeight = target;
    }

    _tweenHeight() {
      const panel = this.panel;
      const from = panel.getBoundingClientRect().height;

      // Natural target height with the inline pin momentarily lifted
      // (stylesheet heights still apply — see _pinHeight).
      panel.style.height = '';
      const to = panel.getBoundingClientRect().height;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce || Math.abs(to - from) < 1) {
        this._pinHeight(to);
        return;
      }

      // Freeze at the start height, force a reflow so it is the transition
      // baseline, then set the target so the CSS height transition runs. The
      // panel stays pinned at the target afterwards (no release to auto).
      panel.style.height = `${from}px`;
      void panel.offsetHeight;
      panel.style.height = `${to}px`;
      this._lastHeight = to;
    }

    open(trigger) {
      this.lastTrigger = trigger || null;
      this.setAttribute('aria-hidden', 'false');
      window.ScrollLock ? ScrollLock.lock('info-drawer') : (document.body.style.overflow = 'hidden');
      if (this.lastTrigger) this.lastTrigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this._handleKeydown);

      // Pin the panel to its freshly-opened height so later content changes
      // animate from here instead of snapping.
      if (this._resizeObserver && !this._hasFixedPanelHeight()) {
        this.panel.style.height = '';
        requestAnimationFrame(() => this._pinHeight());
      }

      // A drawer can nominate its own focus target with [data-drawer-autofocus]
      // (the search field does). That target is focused SYNCHRONOUSLY, still
      // inside the click's user-activation task, because iOS Safari and Android
      // Chrome only raise the on-screen keyboard for a programmatic .focus() on
      // a text field when it runs inside a user gesture. Deferring it by even
      // one rAF focuses the field but leaves the keyboard shut. AAS-4647
      const preferred = this.querySelector('[data-drawer-autofocus]');
      if (preferred) {
        // Commit the aria-hidden style change before focusing: the drawer is
        // visibility:hidden while closed and focus() on a hidden element is a
        // silent no-op. This requires the drawer's visibility to flip in the
        // SAME tick rather than transitioning — a consumer that opts in with
        // [data-drawer-autofocus] must therefore zero its visibility transition
        // delay on open (see #SearchDrawer in search-modal.css). AAS-4647
        void this.offsetHeight;
        preferred.focus({ preventScroll: true });
        return;
      }

      // Move focus to the first focusable element inside the panel, or the panel itself
      requestAnimationFrame(() => {
        const focusable = this.querySelector('[autofocus], [data-drawer-close], button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])');
        (focusable || this).focus();
      });
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      window.ScrollLock ? ScrollLock.unlock('info-drawer') : (document.body.style.overflow = '');
      document.removeEventListener('keydown', this._handleKeydown);

      // Drop the pinned height and any pending resize so the next open starts
      // fresh from the new content's natural height.
      if (this._resizeObserver && !this._hasFixedPanelHeight()) {
        clearTimeout(this._settleTimer);
        this.panel.style.height = '';
      }
      if (this.lastTrigger) {
        this.lastTrigger.setAttribute('aria-expanded', 'false');
        // focus() on a hidden element is a silent no-op that would strand focus
        // on <body>. A trigger CAN be gone by the time we close: the mobile
        // option sheet dismisses itself in the same click that opens this
        // drawer, taking its "Size guide" button with it. Fall back to another
        // visible control that opens this same drawer — for the size guide that
        // is the variant-picker link, which leads back to the same place.
        const trigger = this._visible(this.lastTrigger) ? this.lastTrigger : this._siblingTrigger();
        if (trigger) trigger.focus();
        this.lastTrigger = null;
      }

      // Let content inside the drawer reset itself for the next open — a form
      // that has been submitted, a message left on screen. Fired after close so
      // listeners can time the reset against the 0.3s panel transition
      // (back-in-stock-form.js does).
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    _visible(el) {
      return !!(el && el.offsetParent !== null);
    }

    // Another on-screen control that opens this same drawer, used as the focus
    // destination when the original trigger has been hidden meanwhile.
    _siblingTrigger() {
      if (!this.id) return null;
      const selector = `[data-drawer-open="${this.id.replace(/"/g, '\\"')}"]`;
      return Array.from(document.querySelectorAll(selector)).find((el) => this._visible(el)) || null;
    }

    _handleKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }

      if (e.key !== 'Tab' || this.getAttribute('aria-hidden') === 'true') return;

      const focusable = Array.from(
        this.querySelectorAll('[autofocus], [data-drawer-close], button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.disabled && element.offsetParent !== null);

      if (focusable.length === 0) {
        e.preventDefault();
        this.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('info-drawer', InfoDrawer);

  // Global wiring: any element with [data-drawer-open="id"] opens the matching drawer
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const id = trigger.getAttribute('data-drawer-open');
    const drawer = document.getElementById(id);
    if (drawer && drawer.tagName === 'INFO-DRAWER') {
      e.preventDefault();
      // If the trigger lives inside the open mobile nav drawer, collapse it
      // so the modal isn't stacked on top of the navigation.
      const navDrawer = trigger.closest('nav-drawer');
      if (navDrawer && typeof navDrawer.close === 'function') navDrawer.close();
      drawer.open(trigger);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('info-drawer[aria-hidden="false"]');
    if (open) open.close();
  });
}
